<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

import {
  TemplatePhonePreview,
  MediaFileInput,
} from 'dashboard/shared/templates/components';
import { WHATSAPP_MEDIA_ACCEPT } from '../constants/whatsappTemplateTypes';
import { useWhatsappMediaHeader } from '../composables/useWhatsappMediaHeader';

const props = defineProps({
  mediaFile: {
    type: File,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  body: {
    type: String,
    default: '',
  },
  footer: {
    type: String,
    default: '',
  },
  bodyExamples: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'update:mediaFile',
  'update:header',
  'update:bodyExamples',
  'submit',
  'back',
]);

const VARIABLE_PATTERN = /\{\{(\d+)\}\}/g;

const { t } = useI18n();

const localFile = ref(props.mediaFile);
const {
  format: detectedFormat,
  previewUrl: previewObjectUrl,
  previewMediaType,
} = useWhatsappMediaHeader(localFile);

watch(
  () => props.mediaFile,
  newFile => {
    if (newFile !== localFile.value) {
      localFile.value = newFile;
    }
  }
);

watch(
  localFile,
  file => {
    emit('update:mediaFile', file);
    emit('update:header', { format: detectedFormat.value || '' });
  },
  { immediate: true }
);

const detectedVariables = computed(() => {
  const matches = props.body.match(VARIABLE_PATTERN) || [];
  return matches
    .map(m => parseInt(m.replace(/[{}]/g, ''), 10))
    .filter((v, i, arr) => arr.indexOf(v) === i)
    .sort((a, b) => a - b);
});

const variablesCount = computed(() => {
  if (detectedVariables.value.length === 0) return 0;
  return Math.max(...detectedVariables.value);
});

const localExamples = ref([...props.bodyExamples]);

watch(
  () => props.bodyExamples,
  newVal => {
    localExamples.value = [...newVal];
  },
  { deep: true }
);

watch(variablesCount, newCount => {
  const currentLength = localExamples.value.length;

  if (newCount > currentLength) {
    for (let i = currentLength; i < newCount; i += 1) {
      localExamples.value.push('');
    }
  } else if (newCount < currentLength) {
    localExamples.value = localExamples.value.slice(0, newCount);
  }

  emit('update:bodyExamples', localExamples.value);
});

const handleExampleInput = () => {
  emit('update:bodyExamples', localExamples.value);
};

const getVariableLabel = n => `{{${n}}}`;
const getVariablePlaceholder = n => `Example for {{${n}}}`;

const mediaTypeInfo = computed(() => {
  const types = {
    IMAGE: {
      label: t('WHATSAPP_TEMPLATES.CREATE.MEDIA.IMAGE'),
      hint: t('WHATSAPP_TEMPLATES.CREATE.MEDIA.IMAGE_HINT'),
      icon: 'i-lucide-image',
    },
    VIDEO: {
      label: t('WHATSAPP_TEMPLATES.CREATE.MEDIA.VIDEO'),
      hint: t('WHATSAPP_TEMPLATES.CREATE.MEDIA.VIDEO_HINT'),
      icon: 'i-lucide-video',
    },
    DOCUMENT: {
      label: t('WHATSAPP_TEMPLATES.CREATE.MEDIA.DOCUMENT'),
      hint: t('WHATSAPP_TEMPLATES.CREATE.MEDIA.DOCUMENT_HINT'),
      icon: 'i-lucide-file-text',
    },
  };
  return types[detectedFormat.value] || null;
});

const isValid = computed(() => {
  if (!localFile.value || !detectedFormat.value) return false;
  if (variablesCount.value > 0) {
    return localExamples.value.every(ex => ex && ex.trim().length > 0);
  }
  return true;
});

const previewMessage = computed(() => {
  let message = props.body;
  localExamples.value.forEach((example, index) => {
    const varNum = index + 1;
    const regex = new RegExp(`\\{\\{${varNum}\\}\\}`, 'g');
    message = message.replace(regex, example || `{{${varNum}}}`);
  });
  return message;
});

const handleSubmit = () => {
  if (!isValid.value) return;
  emit('submit');
};

const handleBack = () => emit('back');
</script>

<template>
  <div class="flex flex-col gap-4">
    <div
      class="text-sm p-3 rounded-lg bg-n-blue/10 text-n-slate-12 border border-n-blue/20"
    >
      <div class="font-medium mb-1">
        {{ t('WHATSAPP_TEMPLATES.CREATE.MEDIA.INFO_TITLE') }}
      </div>
      <div class="text-n-slate-11">
        {{ t('WHATSAPP_TEMPLATES.CREATE.MEDIA.INFO_DESCRIPTION') }}
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div class="flex flex-col gap-4 order-2 lg:order-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WHATSAPP_TEMPLATES.CREATE.MEDIA.FILE_LABEL') }}
        </label>
        <MediaFileInput
          v-model="localFile"
          :accept="WHATSAPP_MEDIA_ACCEPT"
          i18n-prefix="WHATSAPP_TEMPLATES"
        />

        <div
          v-if="detectedFormat && mediaTypeInfo"
          class="flex items-center gap-2 p-3 rounded-lg bg-n-alpha-2 border border-n-weak"
        >
          <i class="text-xl text-n-brand" :class="[mediaTypeInfo.icon]" />
          <div class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('WHATSAPP_TEMPLATES.CREATE.MEDIA.DETECTED_TYPE') }}:
              {{ mediaTypeInfo.label }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ mediaTypeInfo.hint }}
            </span>
          </div>
        </div>

        <div v-if="variablesCount > 0" class="flex flex-col gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WHATSAPP_TEMPLATES.CREATE.FORM.EXAMPLES_LABEL') }}
          </label>
          <span class="text-xs text-n-slate-11">
            {{ t('WHATSAPP_TEMPLATES.CREATE.FORM.EXAMPLES_HINT') }}
          </span>
          <div class="flex flex-col gap-3">
            <div
              v-for="n in variablesCount"
              :key="n"
              class="flex flex-col gap-1"
            >
              <Input
                v-model="localExamples[n - 1]"
                :label="getVariableLabel(n)"
                :placeholder="getVariablePlaceholder(n)"
                :message="!localExamples[n - 1] ? 'Required' : ''"
                :message-type="!localExamples[n - 1] ? 'error' : 'info'"
                @input="handleExampleInput"
              />
            </div>
          </div>
        </div>

        <div v-if="loading" class="flex items-center gap-2 text-n-slate-11">
          <div
            class="w-4 h-4 border-2 border-n-brand border-t-transparent rounded-full animate-spin"
          />
          <span class="text-sm">
            {{ t('WHATSAPP_TEMPLATES.CREATE.MEDIA.UPLOADING') }}
          </span>
        </div>
      </div>

      <TemplatePhonePreview
        :body-message="previewMessage"
        :preview-placeholder="
          t('WHATSAPP_TEMPLATES.CREATE.FORM.BODY_PLACEHOLDER')
        "
        :title="t('WHATSAPP_TEMPLATES.CREATE.FORM.PREVIEW_LABEL') || 'Preview'"
        :media-url="previewObjectUrl"
        :media-type="previewMediaType"
        :footer-text="footer"
        :buttons="[]"
        button-type="quick_reply"
        i18n-prefix="WHATSAPP_TEMPLATES"
      />
    </div>

    <div class="flex items-center justify-between w-full gap-3 pt-2">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('WHATSAPP_TEMPLATES.CREATE.WIZARD.BACK')"
        icon="i-lucide-arrow-left"
        icon-position="left"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleBack"
      />
      <Button
        :label="t('WHATSAPP_TEMPLATES.CREATE.WIZARD.CREATE')"
        icon="i-lucide-check"
        icon-position="right"
        class="w-full"
        type="button"
        :disabled="!isValid || loading"
        :is-loading="loading"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
